/** Deterministic scroll geometry shared by the renderer and flying code characters. */
export const clamp01 = value => Math.max(0, Math.min(1, value));
export const interval = (p, a, b) => clamp01((p - a) / (b - a));
export const BUILD_STEPS = [
  { key: "ground", name: "the platform", launch: 0.13, flight: 0.18, traceStart: 0.30, traceEnd: 0.38 },
  { key: "robot", name: "the robot", launch: 0.32, flight: 0.15, traceStart: 0.46, traceEnd: 0.55 },
  { key: "energy", name: "the energy cells", launch: 0.49, flight: 0.15, traceStart: 0.63, traceEnd: 0.72 },
  { key: "portal", name: "the portal", launch: 0.66, flight: 0.15, traceStart: 0.80, traceEnd: 0.89 },
];
const smooth = x => x * x * (3 - 2 * x);
export function assemblyState(progress, height) {
  const p = clamp01(progress);
  const travel = height * 1.12;
  const camera = travel * (0.9 * smooth(interval(p, 0.10, 0.30)) + 0.1 * interval(p, 0.30, 0.84));
  return {
    p, travel, camera,
    stage: p < 0.13 ? "code" : p < 0.89 ? "wireframe" : p < 0.985 ? "scene" : "play",
    step: BUILD_STEPS.reduce((active, step, index) => p >= step.launch ? index : active, 0),
    fill: interval(p, 0.89, 0.98),
  };
}
export function pointOnPath(points, fraction) {
  if (!points.length) return { x: 0, y: 0, angle: 0 };
  const segments = points.map((a, i) => {
    const b = points[(i + 1) % points.length];
    return { a, b, length: Math.hypot(b.x - a.x, b.y - a.y) };
  });
  let distance = clamp01(fraction) * segments.reduce((sum, segment) => sum + segment.length, 0);
  for (const { a, b, length } of segments) {
    if (distance <= length) {
      const t = length ? distance / length : 0;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, angle: Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI };
    }
    distance -= length;
  }
  return { ...points[0], angle: 0 };
}
export function glyphPose(p, step, index, count, from, to, height) {
  const stagger = count > 1 ? index / (count - 1) * 0.055 : 0;
  const start = step.launch + stagger;
  const t = interval(p, start, start + step.flight);
  const u = 1 - t;
  const bend = (index % 5 - 2) * Math.min(16, height * 0.025);
  return {
    started: p >= start, landed: t >= 1,
    x: u * u * u * from.x + 3 * u * u * t * (from.x + bend) + 3 * u * t * t * to.x + t * t * t * to.x,
    y: u * u * u * from.y + 3 * u * u * t * (from.y + height * 0.42) + 3 * u * t * t * (to.y - height * 0.27) + t * t * t * to.y,
    scale: 1 - interval(t, 0.78, 1) * 0.62,
    rotation: interval(t, 0.72, 1) * to.angle,
  };
}

/** Alpha silhouette, clockwise from the first occupied scanline. No bitmap replacement. */
export function silhouette(data, width, height) {
  const rows = [];
  const stride = Math.max(1, Math.round(height / 48));
  for (let y = 0; y < height; y += stride) {
    let left = width, right = -1;
    for (let x = 0; x < width; x++) if (data[(y * width + x) * 4 + 3] > 150) { left = Math.min(left, x); right = x; }
    if (right >= left) rows.push({ left: left / width, right: right / width, y: y / height });
  }
  if (!rows.length) return [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
  return [{ x: rows[0].left, y: rows[0].y }, ...rows.map(row => ({ x: row.right, y: row.y })), ...rows.reverse().map(row => ({ x: row.left, y: row.y }))];
}
